import React, { useCallback, useEffect, useState } from "react";
import { TdsSyncCard } from "../../../admin/presentation/components";
import { fetchTaxLedgerMap, TaxLedgerMapEntry } from "../../../../services/api";
import { SettingsManagedPanel } from "../components/SettingsManagedPanel";
import { TcsRatesList } from "../components/TcsRatesList";
import { TcsSyncButton } from "../components/TcsSyncButton";
import { TdsRatesTable } from "../components/TdsRatesTable";
import { SettingsLayout } from "../layout/SettingsLayout";

/** What a panel heading says after its list has loaded. */
const countLine = (counts: { active: number; expired: number } | null) => {
  if (counts === null) return null;
  if (counts.active + counts.expired === 0) return null;
  return counts.expired > 0
    ? `${counts.active} active · ${counts.expired} expired`
    : `${counts.active} active`;
};

/**
 * TdsTcsPage — `/settings/tds-tcs`.
 *
 * Two panels, not one merged list, following master-admin's onboarding step: TDS
 * is deducted by the payer and TCS is collected by the seller, they sit under
 * different chapters, and a CA works one at a time. Each gets its own heading,
 * its own count, its own sync and its own add action.
 *
 * The tables are settings' own rather than the /admin curation list reused: that
 * list is a stack of rows with an inline expander, built for a modal, and it does
 * not read as a register. What the tenant curates here are the columns Zoho does
 * not return — threshold, the deductee flags, the section label and the AI
 * context — so those belong in the form behind Edit, not in the register.
 *
 * The AI context matters more than it looks: `tds_classification_service.php`
 * selects rates `WHERE is_expired = 0 AND llm_context <> ''`, so until something
 * is written there the post-cutover TDS classifier has no options at all.
 *
 * THE PAGE OWNS THE LEDGER MAP because both panels render a column from it and
 * the read behind it is one Zoho call for both taxes. It is FIRE-AND-FORGET: the
 * tables render immediately with "—" in that column and fill it in when the read
 * lands, and a failure is swallowed rather than shown. The rates are the point of
 * this screen; which account they post to is context, and context must not be
 * able to take the screen down with it.
 */
export const TdsTcsPage: React.FC = () => {
  // The sync controls and the tables are siblings, so a landed sync has to be
  // announced rather than inherited. One token per panel: a TDS sync has nothing
  // to say to the TCS list.
  const [tdsToken, setTdsToken] = useState(0);
  const [tcsToken, setTcsToken] = useState(0);
  const handleTdsSynced = useCallback(() => setTdsToken((n) => n + 1), []);
  const handleTcsSynced = useCallback(() => setTcsToken((n) => n + 1), []);

  const [tdsCounts, setTdsCounts] = useState<{
    active: number;
    expired: number;
  } | null>(null);
  const [tcsCounts, setTcsCounts] = useState<{
    active: number;
    expired: number;
  } | null>(null);

  const [ledgerMap, setLedgerMap] =
    useState<Record<string, TaxLedgerMapEntry> | null>(null);
  // Bumped whenever a rate is written or a sync lands: the map is Zoho's answer
  // and a new rate is not in it yet.
  const [ledgerToken, setLedgerToken] = useState(0);
  const refreshLedgers = useCallback(() => setLedgerToken((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    // The first read takes the server's cached answer; every later one is a
    // reaction to something having changed, so it asks for a fresh read.
    fetchTaxLedgerMap(ledgerToken + tdsToken + tcsToken > 0)
      .then((result) => {
        if (live) setLedgerMap(result.data.ledgers ?? {});
      })
      .catch(() => {
        // Deliberately silent. The column reads "—" and the page is unaffected;
        // a banner about a context column would be louder than what it costs.
      });
    return () => {
      live = false;
    };
  }, [ledgerToken, tdsToken, tcsToken]);

  return (
    <SettingsLayout wide>
      <SettingsManagedPanel
        title="TDS & TCS"
        description="Tax deducted at source on what you pay, and collected at source on what you sell."
      >
        <section className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
          <div className="flex w-full flex-wrap items-baseline justify-between gap-4">
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-heading-3 font-heading-3 text-default-font">
                TDS rates
              </span>
              {countLine(tdsCounts) && (
                <span className="text-caption font-caption text-subtext-color">
                  {countLine(tdsCounts)}
                </span>
              )}
            </div>
            <TdsSyncCard
              showHeader={false}
              showManageAction={false}
              onApplied={handleTdsSynced}
            />
          </div>
          <TdsRatesTable
            reloadToken={tdsToken}
            ledgerMap={ledgerMap}
            onCounts={setTdsCounts}
            onZohoWrite={refreshLedgers}
          />
        </section>

        <section className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
          <div className="flex w-full flex-wrap items-baseline justify-between gap-4">
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-heading-3 font-heading-3 text-default-font">
                TCS rates
              </span>
              {countLine(tcsCounts) && (
                <span className="text-caption font-caption text-subtext-color">
                  {countLine(tcsCounts)}
                </span>
              )}
            </div>
            {/* Moved up here from inside the list, so the two panels have the
                same head: title, count, sync. */}
            <TcsSyncButton onApplied={handleTcsSynced} />
          </div>
          <TcsRatesList
            reloadToken={tcsToken}
            ledgerMap={ledgerMap}
            onCounts={setTcsCounts}
            onZohoWrite={refreshLedgers}
          />
        </section>
      </SettingsManagedPanel>
    </SettingsLayout>
  );
};
